import React, { useEffect, useState } from 'react';
import AdminShell from './AdminShell';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Upload, Save, Trash2, Loader2, Plus } from 'lucide-react';

const defaultKeys = [
  { key: 'hero_image', label: 'Home hero image' },
  { key: 'briefingiq_screenshot', label: 'BriefingIQ product screenshot' },
  { key: 'eventiq_screenshot', label: 'EventIQ product screenshot' },
  { key: 'platform_screenshot', label: 'Platform integrations screenshot' },
  { key: 'about_team', label: 'Company / about team photo' },
];

const AdminImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/images');
      setImages(res.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const merged = () => {
    const map = Object.fromEntries(images.map((i) => [i.key, i]));
    const items = defaultKeys.map((d) => map[d.key] || { key: d.key, label: d.label, url: '' });
    const extras = images.filter((i) => !defaultKeys.some((d) => d.key === i.key));
    return [...items, ...extras];
  };

  const setDraft = (key, field, value) => {
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: value } }));
  };

  const upload = async (key, file) => {
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDraft(key, 'url', res.data.url);
      toast({ title: 'Uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async (item) => {
    const draft = drafts[item.key] || {};
    const payload = {
      key: item.key,
      label: draft.label || item.label,
      url: draft.url ?? item.url,
    };
    if (!payload.url) {
      toast({ title: 'Provide URL or upload an image', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/admin/images', payload);
      toast({ title: 'Saved' });
      setDrafts((d) => {
        const nd = { ...d };
        delete nd[item.key];
        return nd;
      });
      load();
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const remove = async (key) => {
    if (!confirm(`Remove image "${key}"?`)) return;
    try {
      await api.delete(`/admin/images/${key}`);
      toast({ title: 'Removed' });
      load();
    } catch (err) {
      toast({ title: 'Remove failed', variant: 'destructive' });
    }
  };

  const addCustom = () => {
    const key = prompt('New image key (e.g. blog_hero_2)');
    if (!key) return;
    setImages((prev) => [...prev, { key, label: key, url: '' }]);
  };

  const items = merged();

  return (
    <AdminShell title="Images">
      <div className="flex justify-end mb-4">
        <button onClick={addCustom} className="btn-secondary"><Plus className="w-4 h-4" /> Add key</button>
      </div>

      {loading ? (
        <div className="text-ink-3 text-sm">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((item) => {
            const draftUrl = drafts[item.key]?.url ?? item.url;
            return (
              <div key={item.key} className="bg-white rounded-2xl border border-[var(--line-2)] p-5 space-y-3">
                <div className="flex items-baseline justify-between">
                  <div className="text-sm font-semibold text-ink">{item.label}</div>
                  <code className="text-[10px] text-ink-3">{item.key}</code>
                </div>
                {draftUrl ? (
                  <img src={draftUrl} alt={item.key} className="w-full aspect-[16/9] object-cover rounded-lg border border-[var(--line-2)]" />
                ) : (
                  <div className="aspect-[16/9] rounded-lg bg-[var(--bg-alt)] flex items-center justify-center text-xs text-ink-3">
                    No image
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="btn-secondary flex-1 justify-center cursor-pointer">
                    {uploadingKey === item.key ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload</>}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(item.key, e.target.files[0])} />
                  </label>
                  <button onClick={() => save(item)} className="btn-primary flex-1 justify-center">
                    <Save className="w-4 h-4" /> Save
                  </button>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-ink-3">Or paste URL</Label>
                  <Input
                    value={draftUrl}
                    onChange={(e) => setDraft(item.key, 'url', e.target.value)}
                    className="mt-1 rounded-lg border-[var(--line-2)] text-xs"
                    placeholder="https://…"
                  />
                </div>
                {item.url && (
                  <button onClick={() => remove(item.key)} className="text-xs text-ink-3 hover:text-red-600 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminImages;
